// src/utils/passwordValidation.js

/**
 * Password validation utility functions
 * Ensures consistent password requirements across the application
 */

/**
 * Validate password strength with detailed requirements
 * @param {string} password - The password to validate
 * @returns {object} Validation result object
 */
export const validatePasswordStrength = (password) => {
  if (!password) {
    return {
      isValid: false,
      minLength: false,
      hasUpper: false,
      hasLower: false,
      hasNumber: false,
      hasSpecial: false,
      errors: ['Password is required']
    };
  }

  const minLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  const errors = [];
  if (!minLength) errors.push('Password must be at least 8 characters long');
  if (!hasLower) errors.push('Password must contain at least one lowercase letter');
  if (!hasUpper) errors.push('Password must contain at least one uppercase letter');
  if (!hasNumber) errors.push('Password must contain at least one number');
  if (!hasSpecial) errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');

  return {
    isValid: minLength && hasUpper && hasLower && hasNumber && hasSpecial,
    minLength,
    hasUpper,
    hasLower,
    hasNumber,
    hasSpecial,
    errors
  };
};

/**
 * Simple password validation that returns first error message
 * @param {string} password - The password to validate
 * @returns {string} Error message or empty string if valid
 */
export const validatePassword = (password) => {
  const validation = validatePasswordStrength(password);
  return validation.errors[0] || '';
};

/**
 * Validate password confirmation
 * @param {string} confirmPassword - The confirmation password
 * @param {string} originalPassword - The original password
 * @returns {string} Error message or empty string if valid
 */
export const validateConfirmPassword = (confirmPassword, originalPassword) => {
  if (!confirmPassword) return 'Please confirm your password';
  if (confirmPassword !== originalPassword) {
    return 'Passwords do not match';
  }
  return '';
};

/**
 * Get password strength level (for UI indicators)
 * @param {string} password - The password to evaluate
 * @returns {object} Strength information
 */
export const getPasswordStrength = (password) => {
  const validation = validatePasswordStrength(password);
  const score = [
    validation.minLength,
    validation.hasUpper,
    validation.hasLower,
    validation.hasNumber,
    validation.hasSpecial
  ].filter(Boolean).length;

  let strength = 'weak';
  let color = '#ef4444'; // red
  
  if (score >= 5) {
    strength = 'strong';
    color = '#10b981'; // green
  } else if (score >= 3) {
    strength = 'medium';
    color = '#f59e0b'; // yellow
  }

  return {
    score,
    maxScore: 5,
    strength,
    color,
    percentage: (score / 5) * 100
  };
};
